import React, { useState, useEffect } from 'react'
import Patient from 'model/Patient'
import TextInputWithLabelFormGroup from 'components/input/TextInputWithLabelFormGroup'
import { useTranslation } from 'react-i18next'
import TextFieldWithLabelFormGroup from 'components/input/TextFieldWithLabelFormGroup'
import { Alert, Button, Checkbox, Container, Panel, Spinner } from '@hospitalrun/components'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'store'
import Axios from 'axios'
import classes from '../../../../style.module.scss'
import { ComponentWrapperWithAITracking } from 'analytics/AppInsights'
import MedicalTest from 'model/MedicalTest'
import Permissions from 'model/Permissions'
import { beforeUnloadSetting } from 'utils/alertUser'
import PatientsDiagnosisTopics from 'model/PatientsDiagnosisTopics'
import PatientsDiagnosis from 'model/PatientsDiagnosis'
import PatientsDiagnosisMedications from 'model/PatientsDiagnosisMedications'
import { Utils } from 'utils/Utils'
import { fetchPatientExaminations } from 'vdg-patients/vdg-patients-diagnosis-slice'

const { server } = (window as any).config

interface Props {
  patient: Patient
  medicalTest: MedicalTest
  closeParentModal?: any
  topic: PatientsDiagnosisTopics
}

const VdgExternalTestReferral = (props: Props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { userSession } = useSelector((state: RootState) => state.userSession)
  const { permissions } = useSelector((state: RootState) => state.user)
  const [date, setDate] = useState('')
  const [title, setTitle] = useState('')
  const [text, setText] = useState('')
  const { patient, medicalTest, closeParentModal, topic } = props
  const { diagnosis, medications } = useSelector((state: RootState) => state.patientDiagnosis)

  const [isEditable, setIsEditable] = useState(false)
  const [invalidFields, setInvalidFields] = useState({} as any)
  const [errorMessage, setErrorMessage] = useState('')
  const [feedbackFields, setFeedbackFields] = useState({} as any)

  const [diagnoses, setDiagnoses] = useState([] as (PatientsDiagnosis & { selected?: boolean })[])
  const [referralMedications, setReferralMedications] = useState(
    [] as (PatientsDiagnosisMedications & { selected?: boolean })[],
  )

  const [isFetchingDocument, setIsFetchingDocument] = useState(false)

  useEffect(beforeUnloadSetting())

  useEffect(() => {
    setTitle(medicalTest.name)
    setIsEditable(true)
    setDate(new Date().toISOString())

    setDiagnoses(
      diagnosis.map((diagnose: PatientsDiagnosis) => {
        return {
          ...diagnose,
          selected: topic.patientDiagnoses
            ?.filter((d: { active: boolean }) => d.active)
            .some(pd => pd.diagnosisCode === diagnose.diagnosisCode),
        }
      }),
    )

    setReferralMedications(
      medications
        .filter(medication => {
          return medication.active
        })
        .map((medication: PatientsDiagnosisMedications) => {
          return {
            ...medication,
            selected:
              Utils.intersect(
                medication?.patientDiagnoses?.map(dis => dis.diagnosisCode),
                topic?.patientDiagnoses
                  ?.filter((d: { active: boolean }) => d.active)
                  ?.map((dis: any) => dis.diagnosisCode),
              )?.length > 0,
          }
        }),
    )
  }, [patient, diagnosis, medications])

  const onDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const changedDate = event.target.value
    setDate(changedDate)
  }

  const clearValidation = () => {
    setInvalidFields({})
    setFeedbackFields({})
    setErrorMessage('')
  }

  const onTextChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const updatedText = event.target.value
    setText(updatedText)
    clearValidation()
  }

  const validateField = (field: any, fieldName: string) => {
    if (!field) {
      setErrorMessage(t('patient.referral.createReferralError'))
      setInvalidFields((prevState: any) => ({
        ...prevState,
        [fieldName]: true,
      }))
      setFeedbackFields((prevState: any) => ({
        ...prevState,
        [fieldName]: t('patient.referral.' + fieldName) + ' ' + t('patient.required'),
      }))

      return false
    }
    return true
  }

  const validateInput = () => {
    return validateField(title, 'title') && validateField(text, 'text')
  }

  const saveReferral = () => {
    if (!validateInput()) {
      return
    }

    let referralDetails: any = {}
    referralDetails.medicalTest = medicalTest
    referralDetails.diagnoses = diagnoses
      .filter(diagnose => diagnose.selected)
      .map(diagnose => {
        return { code: diagnose.diagnosisCode, name: diagnose.diagnosisName, active: true }
      })
    referralDetails.medications = referralMedications
      .filter(medication => medication.selected)
      .map(medication => {
        const { name, quantity, quantityType, days, times, per, active } = medication
        return { name, quantity, quantityType, days, times, per, active }
      })

    let body: any = {
      referralDetails: {
        title,
        text,
        ...referralDetails,
      },
      patientDiagnoses: [
        ...topic.patientDiagnoses
          ?.filter((d: { active: boolean }) => d.active)
          ?.map((pd: any) => {
            return { diagnosisCode: pd.diagnosisCode, diagnosisId: pd.diagnosisId }
          }),
      ],
      date,
      patient,
      user: userSession,
      timezoneOffset: new Date().getTimezoneOffset(),
    }

    setIsFetchingDocument(true)

    Axios.post(server + '/pdf/medicalTestReferral', body)
      .then(response => {
        closeParentModal()
        dispatch(fetchPatientExaminations(patient.id))

        window.open(server + `/fetchDocument/${patient.id}/${response.data.fileName}.pdf`)
        setIsFetchingDocument(false)
      })
      .catch(err => {
        console.error(err)
        setIsFetchingDocument(false)
      })
  }

  const getBottomButtons = () => {
    if (isFetchingDocument) {
      return (
        <div className="row float-right">
          <Spinner color="blue" loading size={[10, 25]} type="ScaleLoader" />{' '}
        </div>
      )
    } else
      return (
        <>
          <div className="row float-right">
            <div className="btn-group btn-group-lg">
              <Button className="mr-2" key="saveReferral" color="success" onClick={saveReferral}>
                {t('patient.referral.save')}
              </Button>
            </div>
          </div>
        </>
      )
  }

  return (
    <div className={classes.page_wrapper} style={{ background: 'white' }}>
      {errorMessage && <Alert className="alert" color="danger" message={errorMessage} />}
      <div className="row">
        <div className="col">
          <TextInputWithLabelFormGroup
            label={t('patient.referral.date')}
            name="date"
            value={date.slice(0, 10)}
            isEditable={false}
            onChange={onDateChange}
            type="text"
            isRequired
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <TextInputWithLabelFormGroup
            label={t('patient.referral.title')}
            placeholder={t('patient.referral.title')}
            name="title"
            value={title}
            isEditable={false}
            isRequired
            isInvalid={invalidFields?.title}
            feedback={feedbackFields?.title}
          />
        </div>
      </div>

      <div className="row">
        <div className="col">
          <div className="form-group">
            <TextFieldWithLabelFormGroup
              label={t('patient.referral.text')}
              isInvalid={invalidFields.text}
              feedback={feedbackFields?.text}
              name="text"
              value={text}
              isEditable={isEditable}
              onChange={onTextChange}
              isRequired
            />
          </div>
        </div>
      </div>

      <div>
        <Panel title={t('scheduling.appointment.diagnoses')} color="primary">
          <Container className={classes.container}>
            {diagnoses.map(diagnose => (
              <Checkbox
                disabled={!permissions.includes(Permissions.WriteReferral)}
                checked={diagnose.selected}
                label={diagnose.diagnosisName}
                name={diagnose.diagnosisName}
                onChange={event => {
                  let updatedTempDiagnoses = [...diagnoses]
                  const index = updatedTempDiagnoses.indexOf(diagnose)
                  updatedTempDiagnoses[index] = { ...diagnose, selected: event.target.checked }
                  setDiagnoses(updatedTempDiagnoses)
                }}
              />
            ))}
          </Container>
        </Panel>
        <br />
        <Panel title={t('scheduling.appointment.medications')} color="primary">
          <Container className={classes.container}>
            {referralMedications.map(medication => (
              <Checkbox
                disabled={!permissions.includes(Permissions.WriteReferral)}
                checked={medication.selected}
                label={medication.name}
                name={medication.name}
                onChange={event => {
                  let updatedTempMedications = [...referralMedications]
                  const index = updatedTempMedications.indexOf(medication)
                  updatedTempMedications[index] = {
                    ...medication,
                    selected: event.target.checked,
                  }
                  setReferralMedications(updatedTempMedications)
                }}
              />
            ))}
          </Container>
        </Panel>
        <br />
      </div>

      {getBottomButtons()}
    </div>
  )
}

export default ComponentWrapperWithAITracking(VdgExternalTestReferral, 'VdgExternalTestReferral')
